import { create } from 'zustand';
import { getNotificationSettings } from '../services/notificationService';

/**
 * @typedef {Object} NotificationSettings
 * @property {NotificationCategory[]} categories - Every toggle, already filtered and ordered by
 *   the API. The four former flat flags (emailNotifications, productUpdates, newsletter,
 *   billingEmails) and DataNet all arrive here now.
 * @property {boolean} hasDataNet - Firm owns DataNet; mirrored into the shared DataNet store
 */

/**
 * @typedef {Object} NotificationCategory
 * @property {string} key
 * @property {string} label
 * @property {string} [description]
 * @property {number} displayOrder
 * @property {boolean} isEnabled
 */

const defaultSettings = {
  // Empty is the normal state, not an error: a category ships hidden and is released
  // server-side, and the API only returns the ones this user is actually offered.
  categories: [],
  hasDataNet: false
};

export const useNotificationStore = create((set, get) => ({
  notificationSettings: defaultSettings,
  isLoading: false,
  isSaving: false,
  hasLoaded: false,
  error: null,

  setNotificationSettings: (notificationSettings) => set({ notificationSettings }),
  
  setLoading: (isLoading) => set({ isLoading }),
  
  setSaving: (isSaving) => set({ isSaving }),
  
  setError: (error) => set({ error }),

  getNotificationSettings: async () => {
    try {
      set({ isLoading: true, error: null });
      
      const result = await getNotificationSettings();
      if (result.success) {
        set({
          notificationSettings: result.data,
          hasLoaded: true,
          error: null
        });
      } else {
        set({ 
          error: result.message || 'Failed to load notification settings.'
        });
      }
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch notification settings.'
      });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Flips one opt-out category. Kept separate from setNotificationSetting because categories are
   * a list of server-described objects, not a flat key on the settings object.
   */
  setNotificationCategory: (categoryKey, value) => {
    const currentSettings = get().notificationSettings;
    const categories = (currentSettings.categories || []).map((category) =>
      category.key === categoryKey ? { ...category, isEnabled: value } : category
    );
    set({
      notificationSettings: { ...currentSettings, categories }
    });
  },

  setNotificationSetting: (setting, value) => {
    const currentSettings = get().notificationSettings;
    const newSettings = {
      ...currentSettings,
      [setting]: value
    };
    set({
      notificationSettings: newSettings
    });
  },

  reset: () => set({
    notificationSettings: defaultSettings,
    isLoading: false,
    isSaving: false,
    error: null
  })
}));
